import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { NavItem } from "@/components/layout/constants/navigation";
import { useGetNavigationMenu } from "@/components/layout/hooks/useGetNavigationMenu";
import { isNavLinkActive } from "@/components/layout/utils/isNavLinkActive";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasCorePlatformEnabled } from "@/utils/accessControl";

type RouteTitle = { title: string };
type ReservationRoutes = Record<string, RouteTitle> & { default: RouteTitle };

const findNavItemByHref = (items: NavItem[], path: string): NavItem | null => {
  for (const item of items) {
    if (item.items && item.items.length > 0) {
      const nested = findNavItemByHref(item.items, path);
      if (nested) {
        return nested;
      }
    }

    if (isNavLinkActive(path, item.href)) {
      return item;
    }
  }

  return null;
};

export const usePageTitle = (): string => {
  const location = useLocation();
  const currentPath = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { t } = useTranslation();
  const { allNavigation } = useGetNavigationMenu();
  const { userProfile } = useAuthenticatedUser();
  const coreEnabled = hasCorePlatformEnabled(userProfile);

  const reservationRoutes = useMemo(
    (): ReservationRoutes => ({
      "/reservations/rooms": {
        title: t("layout.pageTitles.reservations.rooms"),
      },
      "/reservations/guests": {
        title: t("layout.pageTitles.reservations.guests"),
      },
      "/reservations/rooms/new": {
        title: t("layout.pageTitles.reservations.newRoom"),
      },
      default: { title: t("layout.pageTitles.reservations.default") },
    }),
    [t],
  );

  const legalViewTitles = useMemo(
    (): Record<string, string> => ({
      "/privacy-policy": t(
        "layout.superAdminNav.items.settings.privacy_policy",
      ),
      "/terms-of-use": t("layout.superAdminNav.items.settings.terms_of_use"),
    }),
    [t],
  );
  const defaultNavItem = allNavigation[0].items[0];

  const getReservationNavItem = (path: string): RouteTitle => {
    if (path in reservationRoutes) {
      return reservationRoutes[path as keyof typeof reservationRoutes];
    }

    if (path.startsWith("/reservations/guest")) {
      return { title: t("layout.pageTitles.reservations.guest") };
    }

    if (
      path.startsWith("/reservations/rooms/") &&
      !path.startsWith("/reservations/rooms/add") &&
      !path.startsWith("/reservations/rooms/categories")
    ) {
      return { title: t("layout.pageTitles.reservations.editRoom") };
    }

    if (path.startsWith("/reservations/rooms/categories")) {
      return { title: t("layout.pageTitles.reservations.rooms") };
    }

    return reservationRoutes.default;
  };

  const getDefaultNavItem = (path: string) => {
    return (
      allNavigation
        .flatMap((group) => group.items)
        .find((item) => isNavLinkActive(path, item.href)) || defaultNavItem
    );
  };

  const findNavItemByPath = (path: string) => {
    if (path.startsWith("/account")) {
      return { title: t("layout.userMenu.account") };
    }

    if (path.startsWith("/users/groups/new")) {
      return { title: t("users.groups.addHotelGroup", "Add Hotel Group") };
    }

    if (path.startsWith("/services/activity/")) {
      return { title: "Activity Detail" };
    }

    if (
      (path === "/analytics" || path === "/analytics-fd") &&
      searchParams.get("teamId")
    ) {
      return { title: "Team Performance" };
    }

    if (
      (path === "/analytics" || path === "/analytics-fd") &&
      searchParams.get("staffId")
    ) {
      return { title: "Staff Performance" };
    }

    if (
      path.startsWith("/analytics/services/") ||
      path.startsWith("/analytics-fd/services/")
    ) {
      return { title: "Service Details" };
    }

    if (
      path.startsWith("/analytics/services") ||
      path.startsWith("/analytics-fd/services")
    ) {
      return { title: "Services" };
    }

    if (path.startsWith("/guesteria-partners-revenue")) {
      return { title: t("layout.pageTitles.guesteriaPartnersRevenue") };
    }

    if (path.startsWith("/guesteria-partners/")) {
      return { title: t("layout.pageTitles.guesteriaPartnerEdit") };
    }

    if (path.startsWith("/reservations")) {
      return getReservationNavItem(path);
    }

    const legalTitle = legalViewTitles[path];
    if (legalTitle) {
      return { title: legalTitle };
    }

    const navGroupMatch = allNavigation
      .map((group) => findNavItemByHref(group.items, path))
      .find((item): item is NavItem => item !== null);

    return navGroupMatch ?? getDefaultNavItem(path);
  };

  const cleanTitle = (title: string) => {
    return title
      .replace(/\(\d+\)/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  if (currentPath === "/services" && !coreEnabled) {
    return cleanTitle(
      t(
        "layout.pageTitles.guesteriaPartnersServices",
        "Guesteria Partners services",
      ),
    );
  }

  const navItem = findNavItemByPath(currentPath);

  return cleanTitle(navItem.title);
};
